import { BreakPointCondition, UI, useBreakpointIf, Stack } from '@genial/components';
import { Color, Typography } from '@genial/design-tokens';
import { useNavigation } from '@react-navigation/native';
import { Box, Button } from 'native-base';
import { useCallback, useEffect, useMemo } from 'react';
import { useRecoilState } from 'recoil';

import { OrderByModal } from '../../../../../../components/OrderByModal';
import { SignedInNavigationScreenNavigationProp } from '../../../../../../routes/types';
import { productsOrderByAtom } from '../../../../../../store/product/filters';
import { useGlobalProductContext } from '../../contexts';
import { useGlobalProductController } from '../../hooks';

export const ProductOrderBy = () => {
  const { navigate } = useNavigation<SignedInNavigationScreenNavigationProp>();

  const [{ appliedOrderBy }, setProductsFiltersAtom] = useRecoilState(productsOrderByAtom);

  const { hasSomeLoading, orderBy, isDesktop, product } = useGlobalProductContext();
  const { handleOrderBy } = useGlobalProductController();

  const orderByOptions = useMemo(() => {
    const headerItems = product.tableHead;

    return headerItems
      .filter(({ orderValue }) => !!orderValue)
      .map(({ orderValue, title }) => {
        return {
          label: title,
          value: orderValue as string,
        };
      });
  }, [product.tableHead]);

  const getIsOrdering = () => {
    if (orderBy) {
      const currentOrderBy = product.tableHead.find(
        ({ orderValue }) => orderValue === orderBy.value
      );

      return !!currentOrderBy?.title;
    }

    return false;
  };

  useEffect(() => {
    setProductsFiltersAtom((prev) => ({
      ...prev,
      availableOrderBy: orderByOptions,
    }));
  }, [orderByOptions, setProductsFiltersAtom]);

  useEffect(() => {
    handleOrderBy(appliedOrderBy?.value || '', appliedOrderBy?.direction);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedOrderBy]);

  if (!orderByOptions.length) {
    return <Stack />;
  }

  if (isDesktop && !product.hideTableHead) {
    return <></>;
  }

  return (
    <Button
      onPress={() => navigate('AvailableProductsOrderByModal')}
      isDisabled={hasSomeLoading}
      alignItems="center"
      justifyContent="center"
      variant="secondary"
      bg="white"
      w="52px">
      <Box>
        <UI.Icon name="Ordering" size="xxs" />
        {getIsOrdering() && (
          <Box
            w={Typography['fontsize.md']}
            h={Typography['fontsize.md']}
            borderRadius="full"
            background={Color['brand.tertiary.light']}
            position="absolute"
            top={-12}
            right={-22}
          />
        )}
      </Box>
    </Button>
  );
};

export const AvailableProductsOrderByModal = () => {
  const navigation = useNavigation();
  const isMobile = useBreakpointIf(BreakPointCondition.EqualOrDown, 'sm');
  const isTablet = useBreakpointIf(BreakPointCondition.EqualOrDown, 'lg');

  const goBack = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    }
  }, [navigation]);

  const getSliderModalWidth = () => {
    if (isMobile) {
      return 1;
    }
    if (isTablet) {
      return 0.5;
    }
    return 0.3;
  };

  return (
    <OrderByModal
      onClose={goBack}
      sliderModalWidth={getSliderModalWidth()}
      orderByAtom={productsOrderByAtom}
    />
  );
};
